/**
 * ProductFormModal - Create/Edit Product Form
 *
 * BYK-5: Vendor catalog CRUD form for admin dashboard.
 * Supports:
 * - Create new product
 * - Edit existing product
 * - Vendor/category selection
 * - Dimension inputs (W x H x D)
 * - Price with currency
 * - Active/Featured toggles
 * - GLB model upload (3D assets)
 * - Thumbnail upload
 */

import { useState, useMemo, useRef } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { Upload, X, FileBox, Image as ImageIcon, Loader2 } from "lucide-react";
import { generateThumbnailFromGlb, blobToFile } from "@/lib/glb-thumbnail-generator";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  createProduct,
  updateProduct,
  listVendors,
  listCategories,
  requestUpload,
  uploadFile,
  getMimeType,
  type Product,
  type ProductCreate,
} from "@/lib/api";

interface ProductFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product?: Product | null;
}

type FormData = {
  sku: string;
  name: string;
  description: string;
  vendorId: string;
  categoryId: string;
  width: string;
  height: string;
  depth: string;
  unit: "cm" | "mm" | "m" | "in";
  price: string;
  currency: string;
  active: boolean;
  featured: boolean;
  glbKey: string;
  thumbnailKey: string;
};

type FileUploadState = {
  file: File | null;
  uploading: boolean;
  progress: number;
  key: string | null;
  error: string | null;
};

const initialFormData: FormData = {
  sku: "",
  name: "",
  description: "",
  vendorId: "",
  categoryId: "",
  width: "",
  height: "",
  depth: "",
  unit: "cm",
  price: "",
  currency: "ILS",
  active: true,
  featured: false,
  glbKey: "",
  thumbnailKey: "",
};

const initialFileState: FileUploadState = {
  file: null,
  uploading: false,
  progress: 0,
  key: null,
  error: null,
};

export function ProductFormModal({
  open,
  onOpenChange,
  product,
}: ProductFormModalProps) {
  const isEditing = !!product;
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [error, setError] = useState<string | null>(null);
  const [glbUpload, setGlbUpload] = useState<FileUploadState>(initialFileState);
  const [thumbnailUpload, setThumbnailUpload] =
    useState<FileUploadState>(initialFileState);
  const [isGeneratingThumbnail, setIsGeneratingThumbnail] = useState(false);
  const glbInputRef = useRef<HTMLInputElement>(null);
  const thumbnailInputRef = useRef<HTMLInputElement>(null);

  // Load vendors and categories
  const { data: vendorsData } = useQuery({
    queryKey: ["vendors"],
    queryFn: () => listVendors({ limit: 100, active: true }),
    enabled: open,
  });

  const { data: categoriesData } = useQuery({
    queryKey: ["categories"],
    queryFn: () => listCategories({ limit: 100, active: true }),
    enabled: open,
  });

  // Compute initial form data based on product (for editing) or default (for creating)
  const computedInitialData = useMemo((): FormData => {
    if (product) {
      return {
        sku: product.sku,
        name: product.name,
        description: product.description || "",
        vendorId: product.vendorId,
        categoryId: product.categoryId,
        width: product.dimensions?.width?.toString() || "",
        height: product.dimensions?.height?.toString() || "",
        depth: product.dimensions?.depth?.toString() || "",
        unit: product.dimensions?.unit || "cm",
        price: product.price?.toString() || "",
        currency: product.currency || "ILS",
        active: product.active,
        featured: product.featured,
        glbKey: product.glbKey || "",
        thumbnailKey: product.thumbnailKey || "",
      };
    }
    return initialFormData;
  }, [product]);

  // Reset form when dialog opens with new computed data
  const handleOpenChange = (newOpen: boolean) => {
    if (newOpen) {
      setFormData(computedInitialData);
      setError(null);
      setGlbUpload(initialFileState);
      setThumbnailUpload(initialFileState);
    }
    onOpenChange(newOpen);
  };

  // File upload handler
  const handleFileUpload = async (
    file: File,
    type: "glb" | "thumbnail",
    setUploadState: React.Dispatch<React.SetStateAction<FileUploadState>>
  ) => {
    setUploadState({
      file,
      uploading: true,
      progress: 0,
      key: null,
      error: null,
    });

    try {
      // Request presigned upload token (use getMimeType for proper MIME detection)
      const mimeType = getMimeType(file);
      const presign = await requestUpload({
        filename: file.name,
        mimeType,
        entityType: "product",
        fileSize: file.size,
      });

      setUploadState((prev) => ({ ...prev, progress: 30 }));

      // Upload the file
      const result = await uploadFile(presign.token, file);

      setUploadState({
        file,
        uploading: false,
        progress: 100,
        key: result.data?.key || presign.key,
        error: null,
      });

      // Update form data with the key
      if (type === "glb") {
        setFormData((prev) => ({
          ...prev,
          glbKey: result.data?.key || presign.key,
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          thumbnailKey: result.data?.key || presign.key,
        }));
      }
    } catch (err) {
      setUploadState({
        file,
        uploading: false,
        progress: 0,
        key: null,
        error: err instanceof Error ? err.message : "Upload failed",
      });
    }
  };

  const handleGlbSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Upload the GLB file
    await handleFileUpload(file, "glb", setGlbUpload);

    // Auto-generate thumbnail if no thumbnail is currently set
    if (!formData.thumbnailKey && !thumbnailUpload.key) {
      setIsGeneratingThumbnail(true);
      try {
        // Generate thumbnail from GLB
        const thumbnailBlob = await generateThumbnailFromGlb(file, {
          width: 512,
          height: 512,
        });

        // Create a File object with proper name
        const thumbnailFile = blobToFile(
          thumbnailBlob,
          file.name.replace(/\.(glb|gltf)$/i, "-thumbnail.png")
        );

        // Upload the generated thumbnail
        await handleFileUpload(thumbnailFile, "thumbnail", setThumbnailUpload);
      } catch (err) {
        console.error("Failed to generate thumbnail:", err);
        // Don't block the flow, just log the error
        // User can still manually upload a thumbnail
      } finally {
        setIsGeneratingThumbnail(false);
      }
    }
  };

  const handleThumbnailSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file, "thumbnail", setThumbnailUpload);
    }
  };

  const clearGlbUpload = () => {
    setGlbUpload(initialFileState);
    setFormData((prev) => ({ ...prev, glbKey: "" }));
    if (glbInputRef.current) glbInputRef.current.value = "";
  };

  const clearThumbnailUpload = () => {
    setThumbnailUpload(initialFileState);
    setFormData((prev) => ({ ...prev, thumbnailKey: "" }));
    if (thumbnailInputRef.current) thumbnailInputRef.current.value = "";
  };

  // Create mutation
  const createMutation = useMutation({
    mutationFn: createProduct,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      onOpenChange(false);
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<Omit<ProductCreate, "sku">>;
    }) => updateProduct(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      onOpenChange(false);
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate required fields
    if (!formData.sku && !isEditing) {
      setError("SKU is required");
      return;
    }
    if (!formData.name) {
      setError("Name is required");
      return;
    }
    if (!formData.vendorId) {
      setError("Vendor is required");
      return;
    }
    if (!formData.categoryId) {
      setError("Category is required");
      return;
    }

    // Build dimensions if any provided
    const dimensions =
      formData.width || formData.height || formData.depth
        ? {
            width: parseFloat(formData.width) || 0,
            height: parseFloat(formData.height) || 0,
            depth: parseFloat(formData.depth) || 0,
            unit: formData.unit,
          }
        : undefined;

    // Build data payload
    const data: ProductCreate = {
      sku: formData.sku,
      name: formData.name,
      description: formData.description || undefined,
      vendorId: formData.vendorId,
      categoryId: formData.categoryId,
      dimensions,
      price: formData.price ? parseFloat(formData.price) : undefined,
      currency: formData.currency,
      active: formData.active,
      featured: formData.featured,
      glbKey: formData.glbKey || undefined,
      thumbnailKey: formData.thumbnailKey || undefined,
    };

    if (isEditing && product) {
      // Remove SKU from update payload (can't change SKU)
      const { sku: _sku, ...updateData } = data;
      updateMutation.mutate({ id: product.id, data: updateData });
    } else {
      createMutation.mutate(data);
    }
  };

  const isPending =
    createMutation.isPending ||
    updateMutation.isPending ||
    glbUpload.uploading ||
    thumbnailUpload.uploading ||
    isGeneratingThumbnail;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Edit Product" : "Add New Product"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-600">
              {error}
            </div>
          )}

          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="sku">SKU *</Label>
              <Input
                id="sku"
                value={formData.sku}
                onChange={(e) =>
                  setFormData({ ...formData, sku: e.target.value })
                }
                placeholder="e.g., NIGA-SINK-001"
                disabled={isEditing}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="e.g., Niga Stainless Steel Sink"
              />
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Input
              id="description"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              placeholder="Product description..."
            />
          </div>

          {/* Vendor & Category */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Vendor *</Label>
              <Select
                value={formData.vendorId}
                onValueChange={(value) =>
                  setFormData({ ...formData, vendorId: value })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select vendor" />
                </SelectTrigger>
                <SelectContent>
                  {vendorsData?.items.map((vendor) => (
                    <SelectItem key={vendor.id} value={vendor.id}>
                      {vendor.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Category *</Label>
              <Select
                value={formData.categoryId}
                onValueChange={(value) =>
                  setFormData({ ...formData, categoryId: value })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categoriesData?.items.map((category) => (
                    <SelectItem key={category.id} value={category.id}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Dimensions */}
          <div className="space-y-2">
            <Label>Dimensions (W x H x D)</Label>
            <div className="grid grid-cols-4 gap-2">
              <Input
                type="number"
                value={formData.width}
                onChange={(e) =>
                  setFormData({ ...formData, width: e.target.value })
                }
                placeholder="Width"
                min="0"
                step="0.1"
              />
              <Input
                type="number"
                value={formData.height}
                onChange={(e) =>
                  setFormData({ ...formData, height: e.target.value })
                }
                placeholder="Height"
                min="0"
                step="0.1"
              />
              <Input
                type="number"
                value={formData.depth}
                onChange={(e) =>
                  setFormData({ ...formData, depth: e.target.value })
                }
                placeholder="Depth"
                min="0"
                step="0.1"
              />
              <Select
                value={formData.unit}
                onValueChange={(value: "cm" | "mm" | "m" | "in") =>
                  setFormData({ ...formData, unit: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cm">cm</SelectItem>
                  <SelectItem value="mm">mm</SelectItem>
                  <SelectItem value="m">m</SelectItem>
                  <SelectItem value="in">in</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Price */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="price">Price</Label>
              <Input
                id="price"
                type="number"
                value={formData.price}
                onChange={(e) =>
                  setFormData({ ...formData, price: e.target.value })
                }
                placeholder="e.g., 1500"
                min="0"
                step="0.01"
              />
            </div>
            <div className="space-y-2">
              <Label>Currency</Label>
              <Select
                value={formData.currency}
                onValueChange={(value) =>
                  setFormData({ ...formData, currency: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ILS">ILS (₪)</SelectItem>
                  <SelectItem value="USD">USD ($)</SelectItem>
                  <SelectItem value="EUR">EUR (€)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* File Uploads */}
          <div className="grid grid-cols-2 gap-4">
            {/* GLB Model Upload */}
            <div className="space-y-2">
              <Label>3D Model (GLB)</Label>
              <input
                ref={glbInputRef}
                type="file"
                accept=".glb,.gltf"
                onChange={handleGlbSelect}
                className="hidden"
              />
              {formData.glbKey || glbUpload.file ? (
                <div className="flex items-center gap-2 rounded-md border border-input bg-background px-3 py-2">
                  <FileBox className="h-4 w-4 text-blue-500" />
                  <span className="flex-1 truncate text-sm">
                    {glbUpload.file?.name || formData.glbKey?.split("/").pop()}
                  </span>
                  {glbUpload.uploading ? (
                    <span className="text-xs text-muted-foreground">
                      {glbUpload.progress}%
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={clearGlbUpload}
                      className="text-gray-400 hover:text-red-500"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => glbInputRef.current?.click()}
                  disabled={isPending}
                >
                  <Upload className="mr-2 h-4 w-4" />
                  Upload GLB
                </Button>
              )}
              {glbUpload.error && (
                <p className="text-xs text-red-500">{glbUpload.error}</p>
              )}
            </div>

            {/* Thumbnail Upload */}
            <div className="space-y-2">
              <Label>Thumbnail Image</Label>
              <input
                ref={thumbnailInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={handleThumbnailSelect}
                className="hidden"
              />
              {isGeneratingThumbnail ? (
                <div className="flex items-center gap-2 rounded-md border border-input bg-background px-3 py-2">
                  <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />
                  <span className="flex-1 text-sm text-muted-foreground">
                    Generating from 3D model...
                  </span>
                </div>
              ) : formData.thumbnailKey || thumbnailUpload.file ? (
                <div className="flex items-center gap-2 rounded-md border border-input bg-background px-3 py-2">
                  <ImageIcon className="h-4 w-4 text-green-500" />
                  <span className="flex-1 truncate text-sm">
                    {thumbnailUpload.file?.name ||
                      formData.thumbnailKey?.split("/").pop()}
                  </span>
                  {thumbnailUpload.uploading ? (
                    <span className="text-xs text-muted-foreground">
                      {thumbnailUpload.progress}%
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={clearThumbnailUpload}
                      className="text-gray-400 hover:text-red-500"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => thumbnailInputRef.current?.click()}
                  disabled={isPending}
                >
                  <Upload className="mr-2 h-4 w-4" />
                  Upload Image
                </Button>
              )}
              {thumbnailUpload.error && (
                <p className="text-xs text-red-500">{thumbnailUpload.error}</p>
              )}
            </div>
          </div>

          {/* Status Toggles */}
          <div className="flex gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.active}
                onChange={(e) =>
                  setFormData({ ...formData, active: e.target.checked })
                }
                className="h-4 w-4 rounded border-gray-300"
              />
              <span className="text-sm">Active</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.featured}
                onChange={(e) =>
                  setFormData({ ...formData, featured: e.target.checked })
                }
                className="h-4 w-4 rounded border-gray-300"
              />
              <span className="text-sm">Featured</span>
            </label>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending
                ? "Saving..."
                : isEditing
                  ? "Update Product"
                  : "Create Product"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
